import { useContext, useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { EShopCategory } from '~/src/api/content/shop/getShopItems';
import { FullBuyContext, TFullBuyContextProperties } from '~/src/features/full-buy/analytics/context';
import isAdditionalEquipment from '~/src/features/full-buy/helpers/ab/isAdditionalEquipment';
import { selectProductsByCategory } from '~/src/features/full-buy/store/selectors/products/selectProductsByCategory';
import { ESteps } from '~/src/features/full-buy/types';
import { TState } from '~/src/store';

const EXCLUDE_STEPS = [
    ESteps.agreementCreated,
    ESteps.timeSlots,
    ESteps.final,
    ESteps.equipments,
    ESteps.equipmentsTv,
];

export type TEquipmentComponent = 'all' | 'none' | 'routers' | 'decoders';

interface IUseCheckAdditionalEquipmentResult {
    hasAdditionalEquipment: boolean,
    equipmentComponentType: TEquipmentComponent,
    routerCategory: EShopCategory.Routers | EShopCategory.PrivateSectorEquipment,
}

export const useCheckAdditionalEquipment = (): IUseCheckAdditionalEquipmentResult => {
    const { isVillage } = useContext<TFullBuyContextProperties>(FullBuyContext);
    const routerCategory = EShopCategory.Routers;
    const {
        stepIndex,
        flow,
        routerEquipments,
        decoderEquipments,
    } = useSelector((state: TState) => ({
        stepIndex: state.fullBuy.stepIndex,
        flow: state.fullBuy.flow,
        routerEquipments: selectProductsByCategory(state, routerCategory),
        decoderEquipments: selectProductsByCategory(state, EShopCategory.Decoders),
    }), shallowEqual);

    const step = flow?.steps[stepIndex];
    const hasAdditionalEquipment = isAdditionalEquipment();

    const equipmentComponentType = useMemo(() => {
        if (routerEquipments.length && decoderEquipments.length) {
            return 'all';
        }
        if (routerEquipments.length) {
            return 'routers';
        }
        if (decoderEquipments.length) {
            return 'decoders';
        }
        return 'none';
    }, [routerEquipments, decoderEquipments]);

    if (!hasAdditionalEquipment || (step && EXCLUDE_STEPS.includes(step) || equipmentComponentType === 'none' || isVillage)) {
        return {
            hasAdditionalEquipment: false,
            equipmentComponentType: 'none',
            routerCategory,
        };
    }
    return {
        hasAdditionalEquipment: true,
        equipmentComponentType,
        routerCategory,
    };
};
